import {
  Alert,
  Box,
  Button,
  CloseIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DownIcon,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useTheme
} from "@carrotfertility/carotene-core"
import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import React, { useState } from "react"
import { SupportedLocale, getSupportedLocaleDisplayName } from "./app/IntlMessages"
import { FormattedMessage, useIntl } from "react-intl"

interface UnauthPreferredLocaleProps {
  showModal?: boolean
}

function UnauthPreferredLocale({ showModal }: UnauthPreferredLocaleProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { preferredLocale, setPreferredLocale } = usePreferredLocaleContext()
  const [selectedLocaleId, setSelectedLocaleId] = useState(preferredLocale)
  const intl = useIntl()
  const [modalOpen, setModalOpen] = React.useState(false)
  const theme = useTheme()

  function handleModalClose() {
    setModalOpen(false)
  }

  function getModalText(): JSX.Element {
    if (selectedLocaleId === SupportedLocale.UnitedStatesEnglish) {
      return (
        <FormattedMessage defaultMessage="You've changed your language to U.S. English. Your Carrot app and communications will be in U.S. English. You can update preferred language in your account settings." />
      )
    }

    let selectedLanguage,
      supportedLocation = ""

    if (selectedLocaleId === SupportedLocale.UnitedStatesSpanish) {
      selectedLanguage = intl.formatMessage({ defaultMessage: "Spanish" })
      supportedLocation = intl.formatMessage({ defaultMessage: "the US" })
    } else {
      // CanadianFrench
      selectedLanguage = intl.formatMessage({ defaultMessage: "French" })
      supportedLocation = intl.formatMessage({ defaultMessage: "Canada" })
    }

    return (
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Stack>
          <FormattedMessage
            defaultMessage="You've changed your language to {selectedLanguage}."
            values={{ selectedLanguage: selectedLanguage }}
            description="selectedLanguage options are 'Spanish' and 'French'"
          />
        </Stack>
        <Stack>
          <FormattedMessage
            defaultMessage="If you are employed in {supportedLocation}, your Carrot app and communications will be in {selectedLanguage}. You can update preferred language in your account settings."
            values={{
              supportedLocation: supportedLocation,
              selectedLanguage: selectedLanguage
            }}
            description="supportedLocation options are 'the US' and 'Canada'; selectedLanguage options are 'Spanish' and 'French'"
          />
        </Stack>
        <Stack>
          <Alert>
            <FormattedMessage
              defaultMessage="If you aren't employed in {supportedLocation}, your experience will return to U.S. English once your account is created."
              values={{
                supportedLocation: supportedLocation
              }}
              description="supportedLocation options are 'the US' and 'Canada'"
            />
          </Alert>
        </Stack>
      </Stack>
    )
  }

  // NOTE: this is a hack that means the selector only shows up if the most recently logged-out user has a language other than english
  //  selected. This is a way to make sure it isn't visible for members since the language selection that allows non-english selection *is* behind a flag
  //  will be removed in https://carrotfertility.atlassian.net/browse/WQ-1422
  const { lastUsedLocale } = JSON.parse(localStorage.getItem("intl")) ?? {}
  const showPreferredLocaleSelect = showModal ?? lastUsedLocale !== "en-US"

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (selectedLocaleId?: SupportedLocale) => {
    // this gets passed a SyntheticEvent if user clicks out of dropdown, don't change locale in that case
    if (selectedLocaleId && typeof selectedLocaleId === "string") {
      setSelectedLocaleId(selectedLocaleId)
      setPreferredLocale(selectedLocaleId)
      if (showModal) {
        setModalOpen(true)
      }
    }
    setAnchorEl(null)
  }

  const preferredLocaleOptions = [
    SupportedLocale.UnitedStatesEnglish,
    SupportedLocale.UnitedStatesSpanish,
    SupportedLocale.CanadianFrench
  ]

  return (
    <>
      {showPreferredLocaleSelect && (
        <>
          <Button
            variant="outlined"
            color="secondary"
            id="show-menu"
            aria-label={intl.formatMessage({ defaultMessage: "Open menu" })}
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup={true}
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            {getSupportedLocaleDisplayName(selectedLocaleId)}
            <DownIcon />
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "show-menu"
            }}
          >
            {preferredLocaleOptions.map((locale) => {
              const label = getSupportedLocaleDisplayName(locale)
              return (
                <MenuItem key={locale} value={label} onClick={() => handleClose(locale)}>
                  {label}
                </MenuItem>
              )
            })}
          </Menu>
          {/* For contexts like Registration, we want to show a modal giving user more info about language selection */}
          <Dialog aria-describedby="language-selection-info-modal-description" open={modalOpen} maxWidth="sm">
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end"
              }}
            >
              <IconButton aria-label="close" onClick={handleModalClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContent>
              <DialogContentText id="language-selection-info-modal-description">{getModalText()}</DialogContentText>
            </DialogContent>
            <DialogActions></DialogActions>
          </Dialog>
        </>
      )}
    </>
  )
}

export { UnauthPreferredLocale }
